// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Seven-day (Mon-start) week strip with prev/next week chevrons and a
 * single selected day — the top half of the dashboard Calendar panel
 * (Figma "Dashboard Calendar", node 1827-19074).
 *
 * Pure presentational: the parent owns the selected date and data
 * fetching. Keyboard: left/right arrows move the selection a day at a
 * time within [minDate, maxDate].
 */

import { AppIcon, IconButton } from '@loa/ui-components';
import './WeekStrip.css';

export interface WeekStripProps {
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  minDate: Date;
  maxDate: Date;
  /** Show Sat + Sun columns. Default false — the work week (Mon–Fri). */
  showWeekend?: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Monday 00:00 (local) of the week containing d. */
export function startOfWeek(d: Date): Date {
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const mondayOffset = (day.getDay() + 6) % 7; // Sun=0 → 6, Mon=1 → 0
  return new Date(day.getTime() - mondayOffset * DAY_MS);
}

export function addDays(d: Date, days: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function clampDay(d: Date, min: Date, max: Date): Date {
  if (d < min) return new Date(min);
  if (d > max) return new Date(max);
  return d;
}

export function WeekStrip({
  selectedDate, onSelectDate, minDate, maxDate, showWeekend = false,
}: Readonly<WeekStripProps>) {
  const weekStart = startOfWeek(selectedDate);
  const days = Array.from({ length: showWeekend ? 7 : 5 }, (_, i) => addDays(weekStart, i));

  const prevTarget = clampDay(addDays(selectedDate, -7), minDate, maxDate);
  const nextTarget = clampDay(addDays(selectedDate, 7), minDate, maxDate);
  const canGoPrev = addDays(weekStart, -1) >= startOfWeek(minDate);
  const canGoNext = addDays(weekStart, 7) <= maxDate;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const delta = e.key === 'ArrowLeft' ? -1 : 1;
    let target = addDays(selectedDate, delta);
    // Work-week mode: arrow keys hop over the hidden weekend
    // (Fri → Mon going right, Mon → Fri going left).
    if (!showWeekend) {
      if (target.getDay() === 6) target = addDays(target, delta > 0 ? 2 : -1);
      else if (target.getDay() === 0) target = addDays(target, delta > 0 ? 1 : -2);
    }
    if (target >= minDate && target <= maxDate) {
      onSelectDate(target);
    }
  };

  return (
    <div className="week-strip" role="group" aria-label="Select a day" onKeyDown={handleKeyDown}>
      <IconButton
        aria-label="Previous week"
        variant="ghost"
        disabled={!canGoPrev}
        onClick={() => onSelectDate(prevTarget)}
        icon={<AppIcon name="angle left" />}
      />

      {days.map((day) => {
        const selected = isSameDay(day, selectedDate);
        const weekend = day.getDay() === 0 || day.getDay() === 6;
        const outOfRange = day < startOfWeek(minDate) || day > maxDate;
        const classes = [
          'week-strip__day',
          selected && 'week-strip__day--selected',
          weekend && !selected && 'week-strip__day--muted',
        ].filter(Boolean).join(' ');
        return (
          <button
            key={day.toISOString()}
            type="button"
            className={classes}
            aria-pressed={selected}
            aria-label={day.toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long' })}
            disabled={outOfRange}
            onClick={() => onSelectDate(day)}
          >
            <span className="week-strip__weekday">
              {day.toLocaleDateString('en-IE', { weekday: 'short' })}
            </span>
            <span className="week-strip__number">{day.getDate()}</span>
          </button>
        );
      })}

      <IconButton
        aria-label="Next week"
        variant="ghost"
        disabled={!canGoNext}
        onClick={() => onSelectDate(nextTarget)}
        icon={<AppIcon name="angle right" />}
      />
    </div>
  );
}
